import { COMPLETE, INCOMPLETE } from '@/constants/action';
import type { Action } from '@/types/actions';
import {
  classifyRecurringWeekPeriodKind,
  getRecurringInstancesInRangeCompletionSummary,
  getRecurringSeriesLastCompletedOccurrenceStart,
  hasRecurringOccurrenceInRange,
} from '@/utils/calendar';
import { validateRecurrence } from '@/utils/events';
import { omit } from 'lodash';

const SHADOW_ID_SUFFIX = '__recurringSeriesCompleted';

export function hasRecurringSeriesCompletedOccurrences(action: Action): boolean {
  return getRecurringSeriesLastCompletedOccurrenceStart(action) != null;
}

export function makeRecurringSeriesCompletedShadowRow(action: Action): Action {
  return {
    ...action,
    id: `${action.id}${SHADOW_ID_SUFFIX}`,
    recurringShadowOfId: action.id,
    progressStatus: COMPLETE,
    isRecurringSeriesCompletedRow: true,
    recurringSeriesRowOriginalProgressStatus: action.progressStatus ?? INCOMPLETE,
  };
}

export function augmentCompletedActionsWithRecurringSeries(
  incompleteActions: Action[],
  completedActions: Action[],
): Action[] {
  const existingRealIds = new Set(completedActions.map((a) => a.recurringShadowOfId ?? a.id));
  const shadows: Action[] = [];

  for (const action of incompleteActions) {
    if (action.isRecurringSeriesCompletedRow) continue;
    if (action.isCanceled) continue;
    if (existingRealIds.has(action.id)) continue;
    if (!validateRecurrence(action.event?.recurrence)) continue;
    if (!hasRecurringSeriesCompletedOccurrences(action)) continue;
    shadows.push(makeRecurringSeriesCompletedShadowRow(action));
  }

  return [...completedActions, ...shadows];
}

export type AppendRecurringRangeShadowOptions = {
  requireAllCompleteInRange?: boolean;
};

export function appendRecurringRangeCompleteShadows(
  completedActions: Action[],
  candidateActions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
  options?: AppendRecurringRangeShadowOptions,
): Action[] {
  const requireAll = options?.requireAllCompleteInRange === true;
  const existingRealIds = new Set(completedActions.map((a) => a.recurringShadowOfId ?? a.id));
  const shadows: Action[] = [];

  for (const action of candidateActions) {
    if (action.isRecurringSeriesCompletedRow) continue;
    if (action.isCanceled) continue;
    if (existingRealIds.has(action.id)) continue;
    if (!validateRecurrence(action.event?.recurrence)) continue;
    if (action.progressStatus === COMPLETE) continue;

    const summary = getRecurringInstancesInRangeCompletionSummary(action, rangeStart, rangeEnd);
    if (summary.inRangeCount === 0) continue;
    if (requireAll) {
      if (!summary.allCompleteInRange) continue;
    } else if (!summary.hasCompleteInRange) {
      continue;
    }

    shadows.push(makeRecurringSeriesCompletedShadowRow(action));
    existingRealIds.add(action.id);
  }

  return [...completedActions, ...shadows];
}

export function filterIncompleteActionsForRecurringWeekRange(
  incompleteActions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
): Action[] {
  return incompleteActions.filter((a) => {
    if (!a?.event?.recurrence?.rules?.length) return true;
    if (!validateRecurrence(a.event?.recurrence)) return true;
    if (!hasRecurringOccurrenceInRange(a, rangeStart, rangeEnd)) return false;
    const summary = getRecurringInstancesInRangeCompletionSummary(a, rangeStart, rangeEnd);
    if (summary.inRangeCount > 0 && summary.allCompleteInRange) return false;
    return true;
  });
}

export function mergeWeeklyRecurringCompletedShadows(
  incompleteActions: Action[],
  completedActions: Action[],
  candidateActions: Action[],
  rangeStart: Date,
  rangeEnd: Date,
  now: Date = new Date(),
): Action[] {
  const withRangeShadows = appendRecurringRangeCompleteShadows(
    completedActions,
    candidateActions,
    rangeStart,
    rangeEnd,
  );
  if (classifyRecurringWeekPeriodKind(rangeStart, rangeEnd, now) === 'future') {
    return withRangeShadows;
  }
  return augmentCompletedActionsWithRecurringSeries(incompleteActions, withRangeShadows);
}

export function stripRecurringSeriesShadowRow(item: Action): Action {
  if (!item.isRecurringSeriesCompletedRow || !item.recurringShadowOfId) {
    return item;
  }

  const base = omit(item, [
    'id',
    'recurringShadowOfId',
    'recurringSeriesRowOriginalProgressStatus',
    'isRecurringSeriesCompletedRow',
  ]) as Action;

  return {
    ...base,
    id: item.recurringShadowOfId,
    progressStatus: item.recurringSeriesRowOriginalProgressStatus ?? INCOMPLETE,
  };
}
